import Debug from 'debug';
import * as React from 'react';
import { Image } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { filePartsCompose, filePartsGet, FileStorageType, slugify } from 'gossamer-unified';
import { FormControl } from './FormControl';
import { getConfigOrThrow } from '../ancillary/ConfigManager';
import styles from './File.module.css';

const debug = Debug('gfe/fi/File');

export interface FileUneditableProps {
  imagesOnly: boolean;
  storageType: FileStorageType;
  storeMimeTypeAndTitle: boolean;
  value: string;
}

export interface FileEditableProps extends FileUneditableProps {
  allowedExtensions?: string[];
  className?: string;
  handleChange?: (value: string | null) => void;
  handleClear?: () => void;
  maxSize?: number;
  validity?: boolean;
}

export interface FileProps extends FileEditableProps {
  editable: boolean;
  mandatory: boolean;
}

export const FileUneditable: React.FC<FileUneditableProps> = (props) => {
  if (!props.value) {
    return null;
  }
  const { accessUrl, isImage, title } = getFileProperties(props);
  if (isImage) {
    return <Image fluid={true} thumbnail={true} src={accessUrl} />;
  }
  return (
    <a href={accessUrl} download="download.pdf" className={styles.FileLink} target="_blank">
      {title}
    </a>
  );
};

export const FileEditable: React.FC<FileEditableProps> = (props) => {
  const ref = React.useRef<HTMLInputElement | null>(null);
  const id = uuidv4();
  const { accessUrl, isImage, title } = getFileProperties(props);
  const allowedExtensions = props.allowedExtensions || (isImage && ['image/*']) || undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debug(event.target.value);
    debug(ref.current?.files?.length);
    if (ref.current?.files?.length) {
      const file = ref.current.files[0];
      debug(`name: ${file.name}, size: ${file.size}, type: ${file.type}`);

      const respond = (contentOrUrl: string) => {
        const out = props.storeMimeTypeAndTitle ? filePartsCompose(file.name, file.type, contentOrUrl) : contentOrUrl;
        debug(out);
        props.handleChange?.(out);
      };

      if (props.storageType === FileStorageType.DataURL) {
        convertBlobToDataUrl(file, (dataUrl: string) => {
          const index = dataUrl.indexOf(';base64,');
          const base64 = dataUrl.substring(index + 8);
          respond(base64);
        });
      } else {
        // TODO how to genericize this?
        const path =
          getConfigOrThrow('gossamer-be-origin') +
          getConfigOrThrow('gossamer-api-path') +
          '/main/binary-upload?filename=' +
          encodeURIComponent(slugify(file.name));
        uploadBlob(file, path, respond);
      }
    }
  };

  return (
    <FormControl className={props.className} flexDirection="row" validity={props.validity}>
      <label htmlFor={id}>📎</label>
      <input
        type="file"
        ref={ref}
        accept={allowedExtensions?.join(',')}
        onChange={handleChange}
        id={id}
        style={{
          display: 'none',
        }}
      />
      {!!props.value && !isImage && <div style={{ flexGrow: 1 }}>{title}</div>}
      {!!props.value && isImage && (
        <Image fluid={true} thumbnail={true} src={accessUrl} style={{ width: 'calc(100% - 50px)' }}></Image>
      )}
      {!!props.value && !!props.handleClear && (
        <button onClick={props.handleClear} className="ControlIcon" type="button">
          ✖
        </button>
      )}
    </FormControl>
  );
};

export const File: React.FC<FileProps> = (props) => {
  return (
    <>
      {props.editable && (
        <FileEditable
          allowedExtensions={props.allowedExtensions}
          className={props.className}
          handleChange={props.handleChange}
          handleClear={() => props.handleChange?.(null)}
          imagesOnly={props.imagesOnly}
          storageType={props.storageType}
          storeMimeTypeAndTitle={props.storeMimeTypeAndTitle}
          validity={props.validity}
          value={props.value}
        />
      )}
      {!props.editable && (
        <FileUneditable
          imagesOnly={props.imagesOnly}
          storageType={props.storageType}
          storeMimeTypeAndTitle={props.storeMimeTypeAndTitle}
          value={props.value}
        />
      )}
    </>
  );
};

interface FileProperties {
  title: string;
  accessUrl: string;
  isImage: boolean;
  mimetype?: string;
}

const getFileProperties = (props: FileUneditableProps) => {
  let contentOrUrl: string | null = props.value;
  const out: FileProperties = {
    title: 'Download File',
    accessUrl: props.value,
    isImage: props.imagesOnly,
  };
  if (props.storeMimeTypeAndTitle) {
    const parts = filePartsGet(props.value);
    contentOrUrl = parts.content;
    out.mimetype = parts.mimetype || undefined;
    out.title = parts.title || out.title;
    if (!!props.value && out.mimetype?.indexOf('image/') === 0) {
      out.isImage = true;
    }
    out.accessUrl =
      props.storageType === FileStorageType.DataURL
        ? `data:${out.mimetype};base64,${contentOrUrl || ''}`
        : contentOrUrl || '';
  }
  return out;
};

const convertBlobToDataUrl = (blob: Blob, onConverted: (str: string) => void) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  reader.addEventListener(
    'load',
    () => {
      debug(reader.result as string);
      onConverted(reader.result as string);
    },
    false,
  );
  reader.addEventListener('error', (err) => debug(err));
};

const uploadBlob = (blob: Blob, path: string, onUploaded: (str: string) => void) =>
  fetch(path, {
    credentials: 'include',
    method: 'POST',
    body: blob,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  })
    .then((res) => res.json())
    .then((res2) => {
      debug(res2);
      if (!res2.url) {
        throw new Error('no url provided');
      }
      onUploaded(res2.url);
    })
    .catch(console.error);
